import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiFetch } from "./api";
import LandingPreview from "./LandingPreview";
import RestaurantLogo from "./RestaurantLogo";
import BusinessCardPreview from "./BusinessCardPreview";
import GoogleListingLink from "./GoogleListingLink";
import ColorSwatchPicker from "./ColorSwatchPicker";
import { useRestaurant } from "./RestaurantContext";
import { exportCardImage } from "./exportCardImage";

interface RestaurantData {
  id: number;
  name: string;
  address: string;
  phone?: string;
  cuisine: string;
  logoUrl?: string;
  logoScale?: number;
  ownerId: number;
  welcomeCardCreditGrantedAt?: string | null;
  welcomeCardCreditRedeemedAt?: string | null;
  googleLocationId?: string | null;
  googleLocationTitle?: string | null;
}

interface GoogleStatus {
  configured: boolean;
  connected: boolean;
  email: string | null;
  connectedAt: string | null;
  needsReconnect: boolean;
}

interface QRCode {
  id: number;
  code: string;
  restaurantId: number;
  targetUrl: string;
  createdAt: string;
  active: boolean;
  cardBackgroundColor?: string;
  cardTextColor?: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Mirrors backend/src/index.ts's CARD_ORDER_QUANTITIES -- the server is the
// actual source of truth (rejects anything else), this just drives the UI.
const CARD_ORDER_QUANTITIES = [100, 250, 500, 1000, 2500, 5000] as const;

interface CardOrderPricing {
  amount: number | null;
  currency: string | null;
}

interface CardOrder {
  id: number;
  quantity: number;
  shippingAddress: string;
  status: string;
  stripeAmountCents: number | null;
  stripeCurrency: string | null;
  createdAt: string;
  paidAt: string | null;
  isWelcomeGift: boolean;
  orderedBy: { id: number; name: string | null; email: string } | null;
}

const Restaurant: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [restaurant, setRestaurant] = useState<RestaurantData | null>(null);
  const [loading, setLoading] = useState(true);
  // Confirmed via a 404 that this admin has no restaurant yet, as opposed to the
  // fetch itself failing (network error, backend unreachable, unexpected status).
  // Those two cases must not look the same, since the latter should never show the
  // "no restaurant yet" / Connect Google screen for an admin who actually has one.
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    phone: "",
    cuisine: "",
    logoUrl: "",
    logoScale: 1
  });
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const { currentRestaurantId, restaurants, refreshRestaurants, selectRestaurant, canWrite, canManageQr, canEditLandingPage, canOrderCards } = useRestaurant();
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [addLocationForm, setAddLocationForm] = useState({ name: "", address: "", phone: "", cuisine: "" });
  const [addLocationLoading, setAddLocationLoading] = useState(false);
  const [addLocationError, setAddLocationError] = useState<string | null>(null);
  // G2: with Google connected, a new location can start from one of the
  // owner's Google listings: the form is prefilled (still editable) and the
  // new restaurant is linked to that listing right after it is created.
  const [addLocationListings, setAddLocationListings] = useState<any[] | null>(null);
  const [addLocationListing, setAddLocationListing] = useState<string>('');
  // Name of a location just added without a trial, so we can point its owner at
  // Billing. Null when nothing needs saying.
  const [newLocationNeedsBilling, setNewLocationNeedsBilling] = useState<string | null>(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';
  const [editing, setEditing] = useState(false);
  const [searchParams] = useSearchParams();
  // Back from Google's consent screen (GoogleAuthCallback): open the Google
  // section so the new "Connected as ..." is the first thing they see.
  const justConnectedGoogle = searchParams.get('google') === 'connected';
  const [openSection, setOpenSection] = useState<'restaurant' | 'google' | 'qr' | 'landing' | 'card' | 'danger' | null>(
    justConnectedGoogle ? 'google' : 'restaurant'
  );
  const [googleStatus, setGoogleStatus] = useState<GoogleStatus | null>(null);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [confirmGoogleDisconnect, setConfirmGoogleDisconnect] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const DELETE_CONFIRM_PHRASE = 'Delete restaurant';
  const [qrCode, setQrCode] = useState<QRCode | null>(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState<string | null>(null);
  const [showDeleteQrModal, setShowDeleteQrModal] = useState(false);
  const [deleteQrConfirmInput, setDeleteQrConfirmInput] = useState('');
  const [deletingQr, setDeletingQr] = useState(false);
  const [deleteQrError, setDeleteQrError] = useState<string | null>(null);
  const DELETE_QR_CONFIRM_PHRASE = 'Delete QR code';
  const [qrUrlInput, setQrUrlInput] = useState('');
  const [qrUpdating, setQrUpdating] = useState(false);
  const [landingHeadlineFr, setLandingHeadlineFr] = useState('');
  const [landingHeadlineEn, setLandingHeadlineEn] = useState('');
  const [landingDescriptionFr, setLandingDescriptionFr] = useState('');
  const [landingDescriptionEn, setLandingDescriptionEn] = useState('');
  const [landingButtonTextFr, setLandingButtonTextFr] = useState('');
  const [landingButtonTextEn, setLandingButtonTextEn] = useState('');
  // False (default): one set of text, mirrored server-side into both
  // languages, so there's nothing for a guest to switch. True: the admin maintains
  // separate French/English copy and the guest page offers a language toggle.
  const [landingBilingual, setLandingBilingual] = useState(false);
  // True: guest sees ReviewCounter's own suggested bilingual copy, and the
  // fields below are hidden entirely. False: the admin's own text (above).
  const [landingUseDefaultText, setLandingUseDefaultText] = useState(true);
  const [landingButtonUrl, setLandingButtonUrl] = useState('');
  const [landingPrivacyUrl, setLandingPrivacyUrl] = useState('');
  const [landingLoading, setLandingLoading] = useState(false);
  const [landingError, setLandingError] = useState<string | null>(null);
  const [landingSuccess, setLandingSuccess] = useState(false);
  const [sponsorName, setSponsorName] = useState('');
  const [sponsorUrl, setSponsorUrl] = useState('');
  // Follows landingBilingual like everything else, but has no suggested
  // default, since a sponsorship is specific to this restaurant.
  const [sponsorDescriptionFr, setSponsorDescriptionFr] = useState('');
  const [sponsorDescriptionEn, setSponsorDescriptionEn] = useState('');
  const [oauthLoading, setOauthLoading] = useState(false);
  // Business card design (2026-09-15): seeded from the same qrCode fetch
  // already happening for the QR accordion above, since these two colors live on
  // the QRCode row, no separate fetch needed. Defaults match the printed
  // template's original colors until an admin picks something else.
  const [cardBackgroundColor, setCardBackgroundColor] = useState('#6B8FD2');
  const [cardTextColor, setCardTextColor] = useState('#FFFFFF');
  const [cardSaving, setCardSaving] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [cardSuccess, setCardSuccess] = useState(false);
  const [cardShowGuides, setCardShowGuides] = useState(false);
  // Review Card ordering (2026-09-16).
  const [cardOrderQuantity, setCardOrderQuantity] = useState<number>(CARD_ORDER_QUANTITIES[0]);
  const [cardOrderPricing, setCardOrderPricing] = useState<Record<string, Record<number, CardOrderPricing | null>> | null>(null);
  const [cardOrderShippingChoice, setCardOrderShippingChoice] = useState<'restaurant' | 'custom'>('restaurant');
  const [cardOrderCustomAddress, setCardOrderCustomAddress] = useState('');
  const [cardOrderSubmitting, setCardOrderSubmitting] = useState(false);
  const [cardOrderError, setCardOrderError] = useState<string | null>(null);
  const [cardOrderHistory, setCardOrderHistory] = useState<CardOrder[] | null>(null);
  const [cardOrderHistoryLoading, setCardOrderHistoryLoading] = useState(false);
  // Free welcome-package claim (2026-09-20). Reuses the currently saved
  // design above; no separate color-picking step for the free claim.
  const [welcomeShippingChoice, setWelcomeShippingChoice] = useState<'restaurant' | 'custom'>('restaurant');
  const [welcomeCustomAddress, setWelcomeCustomAddress] = useState('');
  const [welcomeClaiming, setWelcomeClaiming] = useState(false);
  const [welcomeError, setWelcomeError] = useState<string | null>(null);

  useEffect(() => {
    fetchRestaurant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRestaurantId]);

  const fetchQrCode = async () => {
    setQrLoading(true);
    setQrError(null);
    try {
      const res = await apiFetch(`/api/restaurants/${restaurant?.id}/qr`);
      if (res && res.ok) {
        const data = await res.json();
        setQrCode(data);
        setQrUrlInput(data.targetUrl);
      } else {
        setQrCode(null);
      }
    } catch (err) {
      setQrCode(null);
    } finally {
      setQrLoading(false);
    }
  };

  // The Google connection belongs to the owner, not to a location, so it is
  // loaded once rather than per selected restaurant.
  const fetchGoogleStatus = async () => {
    try {
      const res = await apiFetch('/api/google/status');
      if (res.ok) setGoogleStatus(await res.json());
    } catch {
      // Leave the section showing "not connected"; nothing else depends on it.
    }
  };

  useEffect(() => {
    if (!showAddLocation || !googleStatus?.connected || googleStatus.needsReconnect) return;
    apiFetch('/api/google/locations')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setAddLocationListings(Array.isArray(data) ? data.filter((l) => !l.linkedRestaurantId && !l.linkedElsewhere) : null))
      .catch(() => setAddLocationListings(null));
  }, [showAddLocation, googleStatus]);

  useEffect(() => {
    if (isAdmin) fetchGoogleStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    if (restaurant && (isAdmin || canManageQr || canEditLandingPage || canOrderCards)) {
      fetchQrCode();
    }
    // fetchQrCode is redefined every render (closes over restaurant?.id); the
    // deps below are what actually gate re-fetching.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant, isAdmin, canManageQr, canEditLandingPage, canOrderCards]);

  useEffect(() => {
    if (qrCode) {
      setCardBackgroundColor(qrCode.cardBackgroundColor || '#6B8FD2');
      setCardTextColor(qrCode.cardTextColor || '#FFFFFF');
    }
  }, [qrCode]);

  useEffect(() => {
    if (restaurant && qrCode) {
      setLandingLoading(true);
      setLandingError(null);
      apiFetch(`/api/restaurants/${restaurant.id}/landing`, {
        
      })
        .then(res => res.ok ? res.json() : Promise.reject(res))
        .then(data => {
          // Per-language defaults regardless of the admin's own UI language:
          // a fresh QR code shouldn't default to French copy just because an
          // English-speaking admin happens to be looking at an English UI.
          setLandingHeadlineFr(data.headlineFr || t('restaurant.defaultHeadline', { lng: 'fr' }));
          setLandingHeadlineEn(data.headlineEn || t('restaurant.defaultHeadline', { lng: 'en' }));
          setLandingDescriptionFr(data.descriptionFr || t('restaurant.defaultDescription', { lng: 'fr' }));
          setLandingDescriptionEn(data.descriptionEn || t('restaurant.defaultDescription', { lng: 'en' }));
          setLandingButtonTextFr(data.buttonTextFr || t('restaurant.defaultButtonText', { lng: 'fr' }));
          setLandingButtonTextEn(data.buttonTextEn || t('restaurant.defaultButtonText', { lng: 'en' }));
          setLandingBilingual(!!data.bilingual);
          setLandingUseDefaultText(!!data.useDefaultText);
          setLandingButtonUrl(data.buttonUrl || qrCode.targetUrl || '');
          setLandingPrivacyUrl(data.privacyPolicyUrl || '');
          setSponsorName(data.sponsorName || '');
          setSponsorUrl(data.sponsorUrl || '');
          setSponsorDescriptionFr(data.sponsorDescriptionFr || '');
          setSponsorDescriptionEn(data.sponsorDescriptionEn || '');
        })
        .catch(() => setLandingError(t('restaurant.failedLoadLanding')))
        .finally(() => setLandingLoading(false));
    }
  }, [restaurant, qrCode, t]);

  const fetchRestaurant = async () => {
    setLoadFailed(false);
    setError(null);
    try {
      const response = await apiFetch("/api/restaurants/my");
      if (response && response.ok) {
        const data = await response.json();
        // /api/restaurants/my returns an array (an admin can own more than
        // one). Show whichever one the nav switcher currently has selected,
        // falling back to the first while that selection is still resolving.
        const list: RestaurantData[] = Array.isArray(data) ? data : [data];
        setRestaurant(list.find((r) => r.id === currentRestaurantId) ?? list[0] ?? null);
      } else if (response && response.status === 404) {
        // Confirmed: this admin genuinely has no restaurant yet.
        setRestaurant(null);
      } else {
        // apiFetch returned undefined (401 redirect in flight) or some other
        // non-ok status: the backend couldn't be reached or errored, which is
        // not the same thing as "no restaurant".
        setError(t("restaurant.failedToFetch"));
        setLoadFailed(true);
      }
    } catch (err) {
      console.error("Error fetching restaurant:", err);
      setError(t("restaurant.failedToFetch"));
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !restaurant) return;
    setLogoUploading(true);
    setError(null);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('logo', file);
      formDataUpload.append('restaurantId', String(restaurant.id));
      const response = await apiFetch('/api/upload/logo', {
        method: 'POST',
        
        body: formDataUpload,
      } as any);
      if (response.ok) {
        const updatedRestaurant = await response.json();
        setRestaurant(updatedRestaurant);
        // A new file makes any scale tuned for the old one meaningless. The
        // server resets it to 1 on upload, mirror that here so the slider
        // doesn't keep showing a stale position.
        setFormData(f => ({ ...f, logoUrl: updatedRestaurant.logoUrl || '', logoScale: 1 }));
        setLogoPreview(updatedRestaurant.logoUrl || null);
      } else {
        setError(t('restaurant.failedToUploadLogo'));
      }
    } catch (err) {
      setError(t('restaurant.failedToUploadLogo'));
    } finally {
      setLogoUploading(false);
    }
  };

  const handleEdit = () => {
    setFormData({
      name: restaurant?.name || '',
      address: restaurant?.address || '',
      phone: restaurant?.phone || '',
      cuisine: restaurant?.cuisine || '',
      logoUrl: restaurant?.logoUrl || '',
      logoScale: restaurant?.logoScale ?? 1
    });
    setLogoPreview(restaurant?.logoUrl || null);
    setEditing(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setCreating(true);
    try {
      const response = await apiFetch(`/api/restaurants/${restaurant?.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (response && response.ok) {
        const updated = await response.json();
        setRestaurant(updated);
        setEditing(false);
        setLogoPreview(updated.logoUrl || null);
      } else {
        setError(t('restaurant.failedToUpdate'));
      }
    } catch (err) {
      setError(t('restaurant.failedToUpdate'));
    } finally {
      setCreating(false);
    }
  };

  const handleCreateQr = async (e: React.FormEvent) => {
    e.preventDefault();
    setQrUpdating(true);
    setQrError(null);
    try {
      const res = await apiFetch(`/api/restaurants/${restaurant?.id}/qr`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUrl: qrUrlInput })
      });
      if (res && res.ok) {
        const data = await res.json();
        setQrCode(data);
      } else if (res) {
        const errData = await res.json();
        setQrError(errData.error || t('restaurant.failedCreateQr'));
      } else {
        setQrError(t('restaurant.failedCreateQrNoResponse'));
      }
    } catch {
      setQrError(t('restaurant.failedCreateQr'));
    }
    setQrUpdating(false);
  };

  const handleUpdateQr = async (e: React.FormEvent) => {
    e.preventDefault();
    setQrUpdating(true);
    setQrError(null);
    try {
      const res = await apiFetch(`/api/restaurants/${restaurant?.id}/qr`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUrl: qrUrlInput })
      });
      if (res && res.ok) {
        const data = await res.json();
        setQrCode(data);
      } else if (res) {
        const errData = await res.json();
        setQrError(errData.error || t('restaurant.failedUpdateQr'));
      } else {
        setQrError(t('restaurant.failedUpdateQrNoResponse'));
      }
    } catch {
      setQrError(t('restaurant.failedUpdateQr'));
    }
    setQrUpdating(false);
  };

  const handleDeleteQr = async () => {
    setDeletingQr(true);
    setDeleteQrError(null);
    try {
      const res = await apiFetch(`/api/restaurants/${restaurant?.id}/qr`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: deleteQrConfirmInput }),
      });
      if (res && res.ok) {
        setShowDeleteQrModal(false);
        setDeleteQrConfirmInput('');
        // Back to the create-QR form: the code, its scans and opt-ins are gone.
        setQrCode(null);
        setQrUrlInput('');
      } else {
        const data = await res?.json();
        setDeleteQrError(data?.error || t('restaurant.deleteQrFailed'));
      }
    } catch {
      setDeleteQrError(t('restaurant.deleteQrFailed'));
    }
    setDeletingQr(false);
  };

  const handleLandingSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setLandingLoading(true);
    setLandingError(null);
    setLandingSuccess(false);
    try {
      const res = await apiFetch(`/api/restaurants/${restaurant?.id}/landing`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          headlineFr: landingHeadlineFr,
          headlineEn: landingHeadlineEn,
          descriptionFr: landingDescriptionFr,
          descriptionEn: landingDescriptionEn,
          buttonTextFr: landingButtonTextFr,
          buttonTextEn: landingButtonTextEn,
          bilingual: landingBilingual,
          useDefaultText: landingUseDefaultText,
          buttonUrl: landingButtonUrl,
          privacyPolicyUrl: landingPrivacyUrl,
          sponsorName,
          sponsorUrl,
          sponsorDescriptionFr,
          sponsorDescriptionEn,
        }),
      });
      if (res.ok) {
        setLandingSuccess(true);
      } else {
        const err = await res.json();
        setLandingError(err.error || t('restaurant.failedSaveLanding'));
      }
    } catch {
      setLandingError(t('restaurant.failedSaveLanding'));
    }
    setLandingLoading(false);
  };

  const handleCardDesignSave = async () => {
    setCardSaving(true);
    setCardError(null);
    setCardSuccess(false);
    try {
      const res = await apiFetch(`/api/restaurants/${restaurant?.id}/card-design`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cardBackgroundColor, cardTextColor }),
      });
      if (res && res.ok) {
        setCardSuccess(true);
      } else if (res) {
        const err = await res.json();
        setCardError(err.error || t('cardDesign.saveFailed'));
      } else {
        setCardError(t('cardDesign.saveFailed'));
      }
    } catch {
      setCardError(t('cardDesign.saveFailed'));
    }
    setCardSaving(false);
  };

  const fetchCardOrderPricing = async () => {
    try {
      const res = await apiFetch('/api/card-orders/pricing');
      if (res && res.ok) {
        setCardOrderPricing(await res.json());
      }
    } catch {
      // Live pricing is a nice-to-have on top of ordering, not a
      // precondition for it -- leave cardOrderPricing null and let the
      // picker fall back to showing no price rather than a stale guess.
    }
  };

  const fetchCardOrderHistory = async () => {
    setCardOrderHistoryLoading(true);
    try {
      const res = await apiFetch(`/api/restaurants/${restaurant?.id}/card-orders`);
      if (res && res.ok) {
        setCardOrderHistory(await res.json());
      }
    } catch {
      // Leave it null; the history table renders nothing rather than an error.
    } finally {
      setCardOrderHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (openSection === 'card' && (isAdmin || canOrderCards) && restaurant) {
      if (cardOrderPricing === null) fetchCardOrderPricing();
      if (cardOrderHistory === null) fetchCardOrderHistory();
    }
    // Only fetch once per mount (guarded by the null checks above), not on
    // every accordion toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSection, isAdmin, canOrderCards, restaurant]);

  const handlePlaceCardOrder = async () => {
    setCardOrderError(null);
    if (cardOrderShippingChoice === 'restaurant' && !restaurant?.address) {
      setCardOrderError(t('cardDesign.order.noRestaurantAddress'));
      return;
    }
    if (cardOrderShippingChoice === 'custom' && !cardOrderCustomAddress.trim()) {
      setCardOrderError(t('cardDesign.order.customAddressRequired'));
      return;
    }
    setCardOrderSubmitting(true);
    try {
      let frontBlob: Blob;
      let backBlob: Blob;
      try {
        [frontBlob, backBlob] = await Promise.all([
          exportCardImage('front'),
          exportCardImage('back'),
        ]);
      } catch (exportErr) {
        // Logged, not swallowed: this is the one failure mode with no server
        // round-trip to inspect, so the browser console is the only place
        // the real cause (a CORS/CSP block on an inlined image, a missing
        // <svg>, a tainted canvas) shows up.
        console.error('exportCardImage failed:', exportErr);
        setCardOrderError(t('cardDesign.order.exportFailed'));
        return;
      }

      const formData = new FormData();
      formData.append('quantity', String(cardOrderQuantity));
      formData.append('lang', i18n.language?.startsWith('fr') ? 'fr' : 'en');
      formData.append('shippingChoice', cardOrderShippingChoice);
      if (cardOrderShippingChoice === 'custom') {
        formData.append('customAddress', cardOrderCustomAddress.trim());
      }
      formData.append('front', frontBlob, 'front.jpg');
      formData.append('back', backBlob, 'back.jpg');

      const res = await apiFetch(`/api/restaurants/${restaurant?.id}/card-orders`, {
        method: 'POST',
        body: formData,
      });
      if (res && res.ok) {
        const data = await res.json();
        window.location.href = data.checkoutUrl;
        return;
      }
      if (res) {
        const err = await res.json();
        setCardOrderError(err.error || t('cardDesign.order.failed'));
      } else {
        setCardOrderError(t('cardDesign.order.failed'));
      }
    } catch (err) {
      console.error('Card order submission failed:', err);
      setCardOrderError(t('cardDesign.order.failed'));
    } finally {
      setCardOrderSubmitting(false);
    }
  };

  const handleClaimWelcomePackage = async () => {
    setWelcomeError(null);
    if (welcomeShippingChoice === 'restaurant' && !restaurant?.address) {
      setWelcomeError(t('cardDesign.order.noRestaurantAddress'));
      return;
    }
    if (welcomeShippingChoice === 'custom' && !welcomeCustomAddress.trim()) {
      setWelcomeError(t('cardDesign.order.customAddressRequired'));
      return;
    }
    setWelcomeClaiming(true);
    try {
      let frontBlob: Blob;
      let backBlob: Blob;
      try {
        [frontBlob, backBlob] = await Promise.all([
          exportCardImage('front'),
          exportCardImage('back'),
        ]);
      } catch (exportErr) {
        console.error('exportCardImage failed:', exportErr);
        setWelcomeError(t('cardDesign.order.exportFailed'));
        return;
      }

      const formData = new FormData();
      formData.append('shippingChoice', welcomeShippingChoice);
      if (welcomeShippingChoice === 'custom') {
        formData.append('customAddress', welcomeCustomAddress.trim());
      }
      formData.append('front', frontBlob, 'front.jpg');
      formData.append('back', backBlob, 'back.jpg');

      const res = await apiFetch(`/api/restaurants/${restaurant?.id}/card-orders/claim-welcome-gift`, {
        method: 'POST',
        body: formData,
      });
      if (res && res.ok) {
        // Refresh both: the restaurant object flips this section to its
        // redeemed state, and the order shows up in history right away.
        await fetchRestaurant();
        fetchCardOrderHistory();
        return;
      }
      if (res) {
        const err = await res.json();
        setWelcomeError(err.error || t('cardDesign.welcome.failed'));
      } else {
        setWelcomeError(t('cardDesign.welcome.failed'));
      }
    } catch (err) {
      console.error('Welcome-package claim failed:', err);
      setWelcomeError(t('cardDesign.welcome.failed'));
    } finally {
      setWelcomeClaiming(false);
    }
  };

  // Same tab, not a popup: Google sends the browser back to /auth/callback,
  // which finishes the connection and returns here.
  const handleConnectGoogle = async () => {
    setOauthLoading(true);
    setGoogleError(null);
    try {
      const res = await apiFetch('/api/auth/google');
      const data = await res.json().catch(() => ({}));
      if (res.ok && typeof data.url === 'string' && data.url.startsWith('https://accounts.google.com/')) {
        window.location.href = data.url;
        return;
      }
      setGoogleError(t('restaurant.google.connectFailed'));
    } catch {
      setGoogleError(t('restaurant.google.connectFailed'));
    }
    setOauthLoading(false);
  };

  const handleDisconnectGoogle = async () => {
    setGoogleBusy(true);
    setGoogleError(null);
    try {
      const res = await apiFetch('/api/google/disconnect', { method: 'POST' });
      if (!res.ok) {
        setGoogleError(t('restaurant.google.disconnectFailed'));
      } else {
        setConfirmGoogleDisconnect(false);
        await fetchGoogleStatus();
      }
    } catch {
      setGoogleError(t('restaurant.google.disconnectFailed'));
    } finally {
      setGoogleBusy(false);
    }
  };

  const handleDeleteRestaurant = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      const res = await apiFetch(`/api/restaurants/${restaurant?.id}`, {
        method: 'DELETE',
        body: JSON.stringify({ confirm: deleteConfirmInput }),
      });
      if (res && res.ok) {
        setShowDeleteModal(false);
        setDeleteConfirmInput('');
        setRestaurant(null);
        setQrCode(null);
        setOpenSection('restaurant');
      } else {
        const data = await res?.json();
        setDeleteError(data?.error || t('restaurant.deleteFailed'));
      }
    } catch {
      setDeleteError(t('restaurant.deleteFailed'));
    }
    setDeleting(false);
  };

  const handleAddLocation = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddLocationLoading(true);
    setAddLocationError(null);
    try {
      const res = await apiFetch('/api/restaurants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addLocationForm),
      });
      if (res && res.ok) {
        const created = await res.json();
        const fromGoogle = addLocationListings?.find((l) => l.locationId === addLocationListing);
        if (fromGoogle) {
          // Created either way; if linking fails, the owner can still link it
          // from the Google section, so say so rather than undo the create.
          const linkRes = await apiFetch(`/api/restaurants/${created.id}/google/link`, {
            method: 'POST',
            body: JSON.stringify({ accountId: fromGoogle.accountId, locationId: fromGoogle.locationId }),
          }).catch(() => null);
          if (!linkRes || !linkRes.ok) setGoogleError(t('restaurant.google.addLocationLinkFailed'));
        }
        setAddLocationListings(null);
        setAddLocationListing('');
        await refreshRestaurants();
        selectRestaurant(created.id);
        setShowAddLocation(false);
        setAddLocationForm({ name: "", address: "", phone: "", cuisine: "" });
        // The free trial is once per account, so this location starts unpaid:
        // readable but read-only until it has its own subscription. Say so now
        // rather than letting them find out when a reply button does nothing.
        setNewLocationNeedsBilling(created.firstTrial ? null : created.name);
      } else {
        const data = await res?.json();
        setAddLocationError(data?.error || t('restaurant.addLocationFailed'));
      }
    } catch {
      setAddLocationError(t('restaurant.addLocationFailed'));
    }
    setAddLocationLoading(false);
  };

  if (loading) return <div className="p-4 text-center">{t('restaurant.loading')}</div>;

  if (loadFailed) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">{t('restaurant.management')}</h1>
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <h2 className="text-xl font-semibold mb-2 text-red-600">{t('restaurant.loadFailedTitle')}</h2>
          <p className="text-gray-600 mb-4">{error || t('restaurant.failedToFetch')}</p>
          <button
            onClick={fetchRestaurant}
            className="inline-block px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors"
          >
            {t('restaurant.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold">{t('restaurant.management')}</h1>
          {/* On mobile the location switcher lives in the drawer, out of
              sight by default - repeating the name here means there's never
              a doubt about which of several locations is on screen. */}
          {restaurant && restaurants.length > 1 && (
            <p className="text-sm text-gray-500 mt-0.5">
              {t('restaurant.viewingLocation', { name: restaurant.name, count: restaurants.length })}
            </p>
          )}
        </div>
        {isAdmin && restaurant && (
          <button
            onClick={() => setShowAddLocation(true)}
            className="self-start sm:self-auto px-4 py-2 border border-teal-600 text-teal-700 rounded-lg font-semibold hover:bg-teal-50 transition whitespace-nowrap"
          >
            + {t('restaurant.addLocation')}
          </button>
        )}
      </div>

      {newLocationNeedsBilling && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-amber-900">
            {t('restaurant.newLocationNeedsBilling', { name: newLocationNeedsBilling })}
          </p>
          <div className="flex gap-2 shrink-0">
            <Link
              to="/billing"
              className="px-4 py-2 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition whitespace-nowrap"
            >
              {t('restaurant.goToBilling')}
            </Link>
            <button
              onClick={() => setNewLocationNeedsBilling(null)}
              className="px-3 py-2 text-amber-800 font-medium hover:underline"
            >
              {t('common.dismiss')}
            </button>
          </div>
        </div>
      )}

      {showAddLocation && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md space-y-4">
            <h3 className="text-lg font-bold text-gray-900">{t('restaurant.addLocationTitle')}</h3>
            {/* Said before they fill the form, not after they submit it: the
                free trial covers the account's first location only. */}
            <p className="text-sm bg-blue-50 border border-blue-200 text-blue-900 rounded p-3">
              {t('restaurant.addLocationBillingNotice')}
            </p>
            <form onSubmit={handleAddLocation} className="space-y-3">
              {addLocationListings && addLocationListings.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('restaurant.google.addLocationFromGoogle')}</label>
                  <select
                    value={addLocationListing}
                    onChange={(e) => {
                      const id = e.target.value;
                      setAddLocationListing(id);
                      const l = addLocationListings.find((x) => x.locationId === id);
                      if (l) {
                        setAddLocationForm({
                          ...addLocationForm,
                          name: l.title || addLocationForm.name,
                          address: l.address || addLocationForm.address,
                          phone: l.phone || addLocationForm.phone,
                          cuisine: l.category || addLocationForm.cuisine,
                        });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">{t('restaurant.google.addLocationManual')}</option>
                    {addLocationListings.map((l) => (
                      <option key={l.locationId} value={l.locationId}>
                        {l.title}{l.address ? `, ${l.address}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('restaurant.restaurantNameRequired')}</label>
                <input
                  type="text"
                  value={addLocationForm.name}
                  onChange={(e) => setAddLocationForm({ ...addLocationForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('restaurant.addressRequired')}</label>
                <input
                  type="text"
                  value={addLocationForm.address}
                  onChange={(e) => setAddLocationForm({ ...addLocationForm, address: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.phone')}</label>
                <input
                  type="tel"
                  value={addLocationForm.phone}
                  onChange={(e) => setAddLocationForm({ ...addLocationForm, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('restaurant.cuisineRequired')}</label>
                <input
                  type="text"
                  value={addLocationForm.cuisine}
                  onChange={(e) => setAddLocationForm({ ...addLocationForm, cuisine: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                />
              </div>
              {addLocationError && <div className="text-sm text-red-600">{addLocationError}</div>}
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddLocation(false);
                    setAddLocationError(null);
                    setAddLocationForm({ name: "", address: "", phone: "", cuisine: "" });
                    setAddLocationListings(null);
                    setAddLocationListing('');
                  }}
                  disabled={addLocationLoading}
                  className="flex-1 py-2 rounded border font-semibold text-gray-700"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={addLocationLoading}
                  className="flex-1 bg-teal-600 text-white py-2 rounded font-semibold hover:bg-teal-700 disabled:opacity-50"
                >
                  {addLocationLoading ? t('restaurant.addingLocation') : t('restaurant.addLocation')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {restaurant ? (
        <>
          {/* Accordion: Your Restaurant */}
          <div className="bg-white rounded-lg shadow p-6 mb-4">
            <button
              className="w-full flex justify-between items-center px-4 py-3 bg-gray-100 rounded-t-lg focus:outline-none"
              onClick={() => setOpenSection(openSection === 'restaurant' ? null : 'restaurant')}
              aria-expanded={openSection === 'restaurant'}
            >
              <span className="font-semibold text-teal-700">{t('restaurant.yourRestaurant')}</span>
              <svg
                className={`w-5 h-5 transform transition-transform ${openSection === 'restaurant' ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {openSection === 'restaurant' && (
              <div className="pt-4">
          <h2 className="text-xl font-semibold mb-4">{t('restaurant.yourRestaurant')}</h2>
          {isAdmin && !editing && (
            <button
              className="mb-4 px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
              onClick={handleEdit}
            >
              {t('restaurant.editInfo')}
            </button>
          )}
          {editing ? (
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('restaurant.restaurantNameRequired')}</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('restaurant.addressRequired')}</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={e => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('restaurant.phoneOptional')}</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('restaurant.cuisineRequired')}</label>
                <input
                  type="text"
                  value={formData.cuisine}
                  onChange={e => setFormData({ ...formData, cuisine: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('restaurant.logoOptional')}</label>
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={handleLogoChange}
                  className="mb-2"
                  disabled={logoUploading}
                />
                {logoUploading && <div className="text-teal-600 text-sm mb-2">{t('restaurant.uploading')}</div>}
                {logoPreview || formData.logoUrl ? (
                  <div className="mb-2">
                    <RestaurantLogo
                      logoUrl={logoPreview || formData.logoUrl}
                      logoScale={formData.logoScale}
                      alt={t('restaurant.logoPreview')}
                      sizeClassName="w-24 h-24"
                    />
                    <label className="block text-xs font-medium text-gray-500 mt-2 mb-1">
                      {t('restaurant.logoScale')}
                    </label>
                    <input
                      type="range"
                      min={0.5}
                      max={2.5}
                      step={0.05}
                      value={formData.logoScale}
                      onChange={e => setFormData({ ...formData, logoScale: Number(e.target.value) })}
                      className="w-full max-w-xs"
                    />
                  </div>
                ) : null}
                <input
                        type="text"
                  value={formData.logoUrl}
                  onChange={e => setFormData({ ...formData, logoUrl: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder={t('restaurant.logoUrlPlaceholder')}
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={creating}
                  className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50"
                >
                  {creating ? t('common.saving') : t('common.saveChanges')}
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
                  onClick={() => setEditing(false)}
                >
                  {t('common.cancel')}
                </button>
              </div>
              {error && (
                <div className="mt-2 p-2 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>
              )}
            </form>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('restaurant.name')}</label>
                <p className="text-gray-900">{restaurant.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('restaurant.cuisine')}</label>
                <p className="text-gray-900">{restaurant.cuisine}</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('restaurant.address')}</label>
                <p className="text-gray-900">{restaurant.address}</p>
              </div>
              {restaurant.phone && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('restaurant.phone')}</label>
                  <p className="text-gray-900">{restaurant.phone}</p>
                </div>
              )}
              {restaurant.logoUrl && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('restaurant.logo')}</label>
                  <RestaurantLogo
                    logoUrl={restaurant.logoUrl}
                    logoScale={restaurant.logoScale}
                    alt={t('restaurant.logo')}
                    sizeClassName="w-32 h-32"
                  />
                </div>
              )}
            </div>
          )}
          {isAdmin && (
            <div className="mt-6 p-4 bg-teal-50 rounded-lg">
              <p className="text-teal-800 text-sm">
                <strong>{t('restaurant.restaurantId')}</strong> {restaurant.id}
              </p>
              <p className="text-teal-800 text-sm mt-1">
                {t('restaurant.inviteManagersHint')}
              </p>
            </div>
          )}
        </div>
            )}
          </div>
          {/* Accordion: Google Business Profile connection (admin only; the
              connection belongs to the owner, G1 of GOOGLE_SYNC_PLAN.md) */}
          {isAdmin && googleStatus?.configured && (
            <div className="bg-white rounded-lg shadow p-6">
              <button
                className="w-full flex justify-between items-center px-4 py-3 bg-gray-100 rounded-t-lg focus:outline-none"
                onClick={() => setOpenSection(openSection === 'google' ? null : 'google')}
                aria-expanded={openSection === 'google'}
              >
                <span className="font-semibold text-teal-700">
                  {t('restaurant.google.title')}
                  {googleStatus.needsReconnect && (
                    <span className="ml-2 text-xs font-semibold text-red-700 bg-red-50 rounded px-2 py-0.5">{t('restaurant.google.badgeReconnect')}</span>
                  )}
                  {googleStatus.connected && !googleStatus.needsReconnect && (
                    <span className="ml-2 text-xs font-semibold text-teal-800 bg-teal-50 rounded px-2 py-0.5">{t('restaurant.google.badgeConnected')}</span>
                  )}
                </span>
                <svg
                  className={`w-5 h-5 transform transition-transform ${openSection === 'google' ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openSection === 'google' && (
                <div className="pt-4 space-y-4">
                  {justConnectedGoogle && googleStatus.connected && !googleStatus.needsReconnect && (
                    <div className="p-3 rounded bg-teal-50 text-teal-800 text-sm">{t('restaurant.google.justConnected')}</div>
                  )}
                  {googleStatus.needsReconnect ? (
                    <div className="p-3 rounded bg-red-50 text-red-800 text-sm">
                      {t('restaurant.google.needsReconnect', { email: googleStatus.email || '' })}
                    </div>
                  ) : googleStatus.connected ? (
                    <div className="text-sm text-gray-700">
                      <p>
                        {googleStatus.email
                          ? t('restaurant.google.connectedAs', { email: googleStatus.email })
                          : t('restaurant.google.connected')}
                      </p>
                      {googleStatus.connectedAt && (
                        <p className="text-gray-500 mt-1">
                          {t('restaurant.google.connectedOn', { date: new Date(googleStatus.connectedAt).toLocaleDateString() })}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-700 space-y-2">
                      <p>{t('restaurant.google.notConnected')}</p>
                      <p className="text-gray-500">{t('restaurant.google.whichAccountHint')}</p>
                    </div>
                  )}
                  {googleError && <div className="p-3 rounded bg-red-50 text-red-800 text-sm">{googleError}</div>}
                  {googleStatus.connected && !googleStatus.needsReconnect && restaurant && (
                    <GoogleListingLink
                      key={restaurant.id}
                      restaurant={restaurant}
                      onChange={(updated) => {
                        setRestaurant(updated);
                        refreshRestaurants();
                      }}
                    />
                  )}
                  <div className="flex flex-wrap gap-3">
                    {(!googleStatus.connected || googleStatus.needsReconnect) && (
                      <button
                        onClick={handleConnectGoogle}
                        disabled={oauthLoading}
                        className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50"
                      >
                        {oauthLoading
                          ? t('restaurant.connecting')
                          : googleStatus.needsReconnect ? t('restaurant.google.reconnect') : t('restaurant.google.connect')}
                      </button>
                    )}
                    {googleStatus.connected && !confirmGoogleDisconnect && (
                      <button
                        onClick={() => setConfirmGoogleDisconnect(true)}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                      >
                        {t('restaurant.google.disconnect')}
                      </button>
                    )}
                  </div>
                  {confirmGoogleDisconnect && (
                    <div className="p-4 rounded border border-red-200 bg-red-50 space-y-3">
                      <p className="text-sm text-red-800">{t('restaurant.google.disconnectWarning')}</p>
                      <div className="flex gap-3">
                        <button
                          onClick={handleDisconnectGoogle}
                          disabled={googleBusy}
                          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                        >
                          {googleBusy ? t('restaurant.google.disconnecting') : t('restaurant.google.confirmDisconnect')}
                        </button>
                        <button
                          onClick={() => setConfirmGoogleDisconnect(false)}
                          disabled={googleBusy}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-white"
                        >
                          {t('restaurant.google.cancel')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {/* Accordion: QR Code (admin, or a manager delegated canManageQr
              at this location, Fred 2026-09-15) */}
          {(isAdmin || canManageQr) && (
            <div className="bg-white rounded-lg shadow p-6">
              <button
                className="w-full flex justify-between items-center px-4 py-3 bg-gray-100 rounded-t-lg focus:outline-none"
                onClick={() => setOpenSection(openSection === 'qr' ? null : 'qr')}
                aria-expanded={openSection === 'qr'}
              >
                <span className="font-semibold text-teal-700">{t('restaurant.qrAndAnalytics')}</span>
                <svg
                  className={`w-5 h-5 transform transition-transform ${openSection === 'qr' ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openSection === 'qr' && (
                <div className="pt-4 space-y-6">
                  {qrLoading ? (
                    <div className="text-center text-gray-500">{t('restaurant.loadingQr')}</div>
                  ) : qrCode ? (
                    <>
                      {/* QR Code Image & Download */}
                      <div className="flex flex-col items-center">
                        {qrCode.code ? (
                          <img
                            src={`${API_URL}/api/qr/${qrCode.code}/image`}
                            alt={t('restaurant.downloadQr')}
                            className="w-40 h-40 object-contain bg-gray-200 rounded mb-2"
                            style={{ imageRendering: 'crisp-edges' }}
                          />
                        ) : (
                          <div className="w-40 h-40 bg-gray-200 flex items-center justify-center rounded mb-2">
                            <span className="text-gray-400">[QR]</span>
                          </div>
                        )}
                        <a
                          href={qrCode.code ? `${API_URL}/api/qr/${qrCode.code}/image` : undefined}
                          download
                          className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 text-center mt-2"
                          style={{ pointerEvents: qrCode.code ? 'auto' : 'none', opacity: qrCode.code ? 1 : 0.5 }}
                        >
                          {t('restaurant.downloadQr')}
                        </a>
                      </div>
                      <div className="w-full max-w-md mx-auto text-sm text-teal-800 bg-teal-50 border border-teal-200 rounded-md p-3 text-center">
                        {t('restaurant.checkLandingPageHint')}
                      </div>
                      {/* Update QR Code Link */}
                      <form onSubmit={handleUpdateQr} className="flex flex-col items-center gap-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('restaurant.googleReviewLink')}</label>
                        <input
                          type="url"
                          value={qrUrlInput}
                          onChange={e => setQrUrlInput(e.target.value)}
                          className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                          required
                        />
                        <button
                          type="submit"
                          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                          disabled={qrUpdating || !canWrite} title={canWrite ? undefined : t("readOnly.disabledHint")}
                        >
                          {qrUpdating ? t('restaurant.updating') : t('restaurant.updateQrLink')}
                        </button>
                      </form>
                      {qrError && <div className="text-red-600 text-center">{qrError}</div>}
                      {/* Deleting is deliberately admin-only even inside this
                          otherwise-delegated accordion: it's irreversible and
                          wipes scan/opt-in history with it, same severity
                          tier as account deletion, not "QR management". */}
                      {isAdmin && (
                        <div className="flex justify-center">
                          <button
                            type="button"
                            onClick={() => setShowDeleteQrModal(true)}
                            disabled={!canWrite} title={canWrite ? undefined : t("readOnly.disabledHint")}
                            className="text-sm text-red-600 hover:underline disabled:opacity-50"
                          >
                            {t('restaurant.deleteQrButton')}
                          </button>
                        </div>
                      )}
                      <div className="text-center">
                        <Link to="/campaign-analytics" className="text-sm text-teal-700 hover:underline">
                          {t('restaurant.viewCampaignAnalytics')}
                        </Link>
                      </div>
                    </>
                  ) : (
                    <form onSubmit={handleCreateQr} className="flex flex-col items-center gap-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('restaurant.googleReviewLink')}</label>
                      <input
                        type="url"
                        value={qrUrlInput}
                        onChange={e => setQrUrlInput(e.target.value)}
                        className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="https://g.page/r/YourRestaurant/review"
                        required
                      />
                      <button
                        type="submit"
                        className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50"
                        disabled={qrUpdating || !canWrite} title={canWrite ? undefined : t("readOnly.disabledHint")}
                      >
                        {qrUpdating ? t('restaurant.creating') : t('restaurant.createQr')}
                      </button>
                      {qrError && <div className="text-red-600 text-center">{qrError}</div>}
                    </form>
                  )}
            </div>
          )}
            </div>
          )}
          {/* Accordion: Edit Landing Page (admin, or a manager delegated
              canEditLandingPage at this location, and only once a QR code
              exists, since this content lives on the QRCode row, so editing it
              before one exists would just 404 on save). */}
          {(isAdmin || canEditLandingPage) && qrCode && (
            <div className="bg-white rounded-lg shadow p-6 mt-6">
              <button
                className="w-full flex justify-between items-center px-4 py-3 bg-gray-100 rounded-t-lg focus:outline-none"
                onClick={() => setOpenSection(openSection === 'landing' ? null : 'landing')}
                aria-expanded={openSection === 'landing'}
              >
                <span className="font-semibold text-teal-700">{t('restaurant.editLandingPage')}</span>
                <svg
                  className={`w-5 h-5 transform transition-transform ${openSection === 'landing' ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openSection === 'landing' && (
                <div className="pt-4 space-y-6">
                  <form onSubmit={handleLandingSave} className="flex flex-col items-center gap-4 w-full max-w-lg mx-auto">
                    <div className="w-full flex items-center gap-2 bg-stone-50 rounded-md p-3 border border-gray-200">
                      <input
                        type="checkbox"
                        id="landingUseDefaultText"
                        checked={landingUseDefaultText}
                        onChange={e => setLandingUseDefaultText(e.target.checked)}
                      />
                      <label htmlFor="landingUseDefaultText" className="text-sm font-medium text-gray-700">
                        {t('restaurant.useDefaultLandingText')}
                      </label>
                    </div>

                    <div className="w-full flex items-center gap-2 bg-stone-50 rounded-md p-3 border border-gray-200">
                      <input
                        type="checkbox"
                        id="landingBilingual"
                        checked={landingBilingual}
                        onChange={e => setLandingBilingual(e.target.checked)}
                      />
                      <label htmlFor="landingBilingual" className="text-sm font-medium text-gray-700">
                        {t('restaurant.bilingualLanding')}
                      </label>
                    </div>

                    {!landingUseDefaultText && (
                      <>
                    <label className="block text-sm font-medium text-gray-700 mb-1 w-full">
                      {t('restaurant.headline')}{landingBilingual ? ` (${t('common.french')})` : ''}
                    </label>
                    <input
                      type="text"
                      value={landingHeadlineFr}
                      onChange={e => {
                        const v = e.target.value;
                        setLandingHeadlineFr(v);
                        if (!landingBilingual) setLandingHeadlineEn(v);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      maxLength={80}
                      required
                    />
                    {landingBilingual && (
                      <>
                        <label className="block text-sm font-medium text-gray-700 mb-1 w-full">
                          {t('restaurant.headline')} ({t('common.english')})
                        </label>
                        <input
                          type="text"
                          value={landingHeadlineEn}
                          onChange={e => setLandingHeadlineEn(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                          maxLength={80}
                          required
                        />
                      </>
                    )}

                    <label className="block text-sm font-medium text-gray-700 mb-1 w-full">
                      {t('restaurant.description')}{landingBilingual ? ` (${t('common.french')})` : ''}
                    </label>
                    <textarea
                      value={landingDescriptionFr}
                      onChange={e => {
                        const v = e.target.value;
                        setLandingDescriptionFr(v);
                        if (!landingBilingual) setLandingDescriptionEn(v);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      rows={3}
                      maxLength={300}
                      required
                    />
                    {landingBilingual && (
                      <>
                        <label className="block text-sm font-medium text-gray-700 mb-1 w-full">
                          {t('restaurant.description')} ({t('common.english')})
                        </label>
                        <textarea
                          value={landingDescriptionEn}
                          onChange={e => setLandingDescriptionEn(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                          rows={3}
                          maxLength={300}
                          required
                        />
                      </>
                    )}

                    <label className="block text-sm font-medium text-gray-700 mb-1 w-full">
                      {t('restaurant.buttonText')}{landingBilingual ? ` (${t('common.french')})` : ''}
                    </label>
                    <input
                      type="text"
                      value={landingButtonTextFr}
                      onChange={e => {
                        const v = e.target.value;
                        setLandingButtonTextFr(v);
                        if (!landingBilingual) setLandingButtonTextEn(v);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      maxLength={40}
                      required
                    />
                    {landingBilingual && (
                      <>
                        <label className="block text-sm font-medium text-gray-700 mb-1 w-full">
                          {t('restaurant.buttonText')} ({t('common.english')})
                        </label>
                        <input
                          type="text"
                          value={landingButtonTextEn}
                          onChange={e => setLandingButtonTextEn(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                          maxLength={40}
                          required
                        />
                      </>
                    )}
                      </>
                    )}

                    <label className="block text-sm font-medium text-gray-700 mb-1 w-full">{t('restaurant.buttonLink')}</label>
              <input
                      type="url"
                      value={landingButtonUrl}
                      onChange={e => setLandingButtonUrl(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      required
                    />
                    <label className="block text-sm font-medium text-gray-700 mb-1 w-full">{t('restaurant.privacyPolicyUrlOptional')}</label>
              <input
                type="url"
                      value={landingPrivacyUrl}
                      onChange={e => setLandingPrivacyUrl(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder={t('restaurant.privacyPolicyPlaceholder')}
              />
                    <label className="block text-sm font-medium text-gray-700 mb-1 w-full">{t('restaurant.sponsorNameOptional')}</label>
                    <input
                      type="text"
                      value={sponsorName}
                      onChange={e => setSponsorName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      maxLength={60}
                      placeholder={t('restaurant.sponsorNamePlaceholder')}
                    />
                    <label className="block text-sm font-medium text-gray-700 mb-1 w-full">{t('restaurant.sponsorUrlOptional')}</label>
                    <input
                      type="url"
                      value={sponsorUrl}
                      onChange={e => setSponsorUrl(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder={t('restaurant.sponsorUrlPlaceholder')}
                    />
                    <label className="block text-sm font-medium text-gray-700 mb-1 w-full">
                      {t('restaurant.sponsorDescriptionOptional')}{landingBilingual ? ` (${t('common.french')})` : ''}
                    </label>
                    <textarea
                      value={sponsorDescriptionFr}
                      onChange={e => {
                        const v = e.target.value;
                        setSponsorDescriptionFr(v);
                        if (!landingBilingual) setSponsorDescriptionEn(v);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      rows={2}
                      maxLength={200}
                      placeholder={t('restaurant.sponsorDescriptionPlaceholder')}
                    />
                    {landingBilingual && (
                      <>
                        <label className="block text-sm font-medium text-gray-700 mb-1 w-full">
                          {t('restaurant.sponsorDescriptionOptional')} ({t('common.english')})
                        </label>
                        <textarea
                          value={sponsorDescriptionEn}
                          onChange={e => setSponsorDescriptionEn(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                          rows={2}
                          maxLength={200}
                          placeholder={t('restaurant.sponsorDescriptionPlaceholder')}
                        />
                      </>
                    )}
            <button
              type="submit"
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                      disabled={landingLoading || !canWrite} title={canWrite ? undefined : t("readOnly.disabledHint")}
            >
                      {landingLoading ? t('common.saving') : t('restaurant.saveLandingPage')}
            </button>
                    {landingError && <div className="text-red-600 text-center">{landingError}</div>}
                    {landingSuccess && <div className="text-green-600 text-center">{t('common.saved')}</div>}
          </form>
                  {/* Live Preview */}
                  <div className="mt-8 p-6 bg-stone-50 rounded shadow w-full max-w-lg mx-auto">
                    <LandingPreview
                      logoUrl={restaurant?.logoUrl}
                      logoScale={restaurant?.logoScale}
                      restaurantName={restaurant?.name}
                      headlineFr={landingHeadlineFr}
                      headlineEn={landingHeadlineEn}
                      descriptionFr={landingDescriptionFr}
                      descriptionEn={landingDescriptionEn}
                      buttonTextFr={landingButtonTextFr}
                      buttonTextEn={landingButtonTextEn}
                      bilingual={landingBilingual}
                      buttonUrl={landingButtonUrl}
                      privacyPolicyUrl={landingPrivacyUrl}
                      sponsorName={sponsorName}
                      sponsorUrl={sponsorUrl}
                      sponsorDescriptionFr={sponsorDescriptionFr}
                      sponsorDescriptionEn={sponsorDescriptionEn}
                      optInEnabled={true}
                      previewMode={true}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Accordion: Business Card design + ordering. Colors stay
              admin-only to edit (Fred's original framing when delegation was
              built: design wasn't part of it, only ordering, once ordering
              existed), gated per-control below. Ordering itself opens to a
              manager delegated canOrderCards (2026-09-17). Only once a QR
              code exists, since the two colors live on that row, same reasoning
              as the landing accordion above. */}
          {(isAdmin || canOrderCards) && qrCode && (
            <div className="bg-white rounded-lg shadow p-6 mt-6">
              <button
                className="w-full flex justify-between items-center px-4 py-3 bg-gray-100 rounded-t-lg focus:outline-none"
                onClick={() => setOpenSection(openSection === 'card' ? null : 'card')}
                aria-expanded={openSection === 'card'}
              >
                <span className="font-semibold text-teal-700">{t('cardDesign.title')}</span>
                <svg
                  className={`w-5 h-5 transform transition-transform ${openSection === 'card' ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openSection === 'card' && (
                <div className="pt-4 space-y-6">
                  <p className="text-sm text-gray-600">{t('cardDesign.intro')}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2 text-center">{t('cardDesign.frontLabel')}</p>
                      <div className="max-w-[220px] mx-auto">
                        <BusinessCardPreview
                          side="front"
                          restaurant={{ address: restaurant.address, phone: restaurant.phone, logoUrl: restaurant.logoUrl, logoScale: restaurant.logoScale }}
                          qrImageUrl={qrCode.code ? `${API_URL}/api/qr/${qrCode.code}/image` : undefined}
                          cardBackgroundColor={cardBackgroundColor}
                          cardTextColor={cardTextColor}
                          showGuides={cardShowGuides}
                        />
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2 text-center">{t('cardDesign.backLabel')}</p>
                      <div className="max-w-[220px] mx-auto">
                        <BusinessCardPreview
                          side="back"
                          restaurant={{ address: restaurant.address, phone: restaurant.phone, logoUrl: restaurant.logoUrl, logoScale: restaurant.logoScale }}
                          cardBackgroundColor={cardBackgroundColor}
                          cardTextColor={cardTextColor}
                          showGuides={cardShowGuides}
                        />
                      </div>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-600 justify-center">
                    <input type="checkbox" checked={cardShowGuides} onChange={e => setCardShowGuides(e.target.checked)} />
                    {t('cardDesign.showGuides')}
                  </label>
                  {/* Colors stay admin-only to edit -- a delegated manager
                      previews and orders the design as the admin left it. */}
                  {isAdmin && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('cardDesign.backgroundColor')}</label>
                        <ColorSwatchPicker value={cardBackgroundColor} onChange={setCardBackgroundColor} disabled={!canWrite} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('cardDesign.textColor')}</label>
                        <ColorSwatchPicker value={cardTextColor} onChange={setCardTextColor} disabled={!canWrite} />
                      </div>
                      <div className="flex justify-center">
                        <button
                          type="button"
                          onClick={handleCardDesignSave}
                          disabled={cardSaving || !canWrite}
                          title={canWrite ? undefined : t("readOnly.disabledHint")}
                          className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50"
                        >
                          {cardSaving ? t('cardDesign.saving') : t('cardDesign.save')}
                        </button>
                      </div>
                      {cardSuccess && <div className="text-green-600 text-center text-sm">{t('cardDesign.saveSuccess')}</div>}
                      {cardError && <div className="text-red-600 text-center text-sm">{cardError}</div>}
                    </>
                  )}

                  {/* Free annual-plan welcome package (2026-09-20). Only
                      rendered once granted; collapses into a read-only
                      confirmation once redeemed, never disappears outright,
                      so it isn't a "did that actually work?" moment. Reuses
                      the design saved above, no separate color picker. */}
                  {restaurant.welcomeCardCreditGrantedAt && (
                    <div className="pt-6 mt-6 border-t border-gray-200 space-y-4">
                      <h3 className="font-semibold text-teal-700">{t('cardDesign.welcome.title')}</h3>
                      {restaurant.welcomeCardCreditRedeemedAt ? (
                        <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded p-3">
                          {t('cardDesign.welcome.redeemed', {
                            date: new Date(restaurant.welcomeCardCreditRedeemedAt).toLocaleDateString(),
                          })}
                        </p>
                      ) : (
                        <>
                          <p className="text-sm text-gray-600">{t('cardDesign.welcome.intro')}</p>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('cardDesign.order.shippingChoice')}</label>
                            <div className="space-y-2">
                              <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="radio"
                                  name="welcomeShipping"
                                  checked={welcomeShippingChoice === 'restaurant'}
                                  onChange={() => setWelcomeShippingChoice('restaurant')}
                                />
                                {t('cardDesign.order.shipToRestaurant')}{restaurant?.address ? `: ${restaurant.address}` : ''}
                              </label>
                              <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="radio"
                                  name="welcomeShipping"
                                  checked={welcomeShippingChoice === 'custom'}
                                  onChange={() => setWelcomeShippingChoice('custom')}
                                />
                                {t('cardDesign.order.shipToCustom')}
                              </label>
                              {welcomeShippingChoice === 'custom' && (
                                <textarea
                                  value={welcomeCustomAddress}
                                  onChange={(e) => setWelcomeCustomAddress(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                                  rows={2}
                                  maxLength={500}
                                  placeholder={t('cardDesign.order.customAddressPlaceholder')}
                                />
                              )}
                            </div>
                          </div>
                          <div className="flex justify-center">
                            <button
                              type="button"
                              onClick={handleClaimWelcomePackage}
                              disabled={welcomeClaiming || !canWrite}
                              title={canWrite ? undefined : t("readOnly.disabledHint")}
                              className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50"
                            >
                              {welcomeClaiming ? t('cardDesign.welcome.claiming') : t('cardDesign.welcome.claimButton')}
                            </button>
                          </div>
                          {welcomeError && <div className="text-red-600 text-center text-sm">{welcomeError}</div>}
                        </>
                      )}
                    </div>
                  )}

                  {/* Ordering (2026-09-16): quantity + live Stripe price,
                      shipping choice, then a real Checkout Session minted
                      from the matching PrintReady Payment Link's current
                      price (see backend/src/index.ts's card-order route). */}
                  <div className="pt-6 mt-6 border-t border-gray-200 space-y-4">
                    <h3 className="font-semibold text-teal-700">{t('cardDesign.order.title')}</h3>
                    <p className="text-sm text-gray-600">{t('cardDesign.order.intro')}</p>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('cardDesign.order.quantity')}</label>
                      <div className="flex flex-wrap gap-2">
                        {CARD_ORDER_QUANTITIES.map((q) => {
                          const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
                          const price = cardOrderPricing?.[lang]?.[q];
                          return (
                            <button
                              key={q}
                              type="button"
                              onClick={() => setCardOrderQuantity(q)}
                              className={`px-3 py-2 rounded border text-sm text-center ${cardOrderQuantity === q ? 'border-teal-600 bg-teal-50 text-teal-700' : 'border-gray-300 text-gray-700'}`}
                            >
                              <div className="font-medium">{q}</div>
                              <div className="text-xs text-gray-500">
                                {price?.amount != null && price.currency
                                  ? `${(price.amount / 100).toFixed(2)} ${price.currency.toUpperCase()}`
                                  : t('cardDesign.order.priceUnavailable')}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('cardDesign.order.shippingChoice')}</label>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="radio"
                            name="cardOrderShipping"
                            checked={cardOrderShippingChoice === 'restaurant'}
                            onChange={() => setCardOrderShippingChoice('restaurant')}
                          />
                          {t('cardDesign.order.shipToRestaurant')}{restaurant?.address ? `: ${restaurant.address}` : ''}
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="radio"
                            name="cardOrderShipping"
                            checked={cardOrderShippingChoice === 'custom'}
                            onChange={() => setCardOrderShippingChoice('custom')}
                          />
                          {t('cardDesign.order.shipToCustom')}
                        </label>
                        {cardOrderShippingChoice === 'custom' && (
                          <textarea
                            value={cardOrderCustomAddress}
                            onChange={(e) => setCardOrderCustomAddress(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                            rows={2}
                            maxLength={500}
                            placeholder={t('cardDesign.order.customAddressPlaceholder')}
                          />
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">{t('cardDesign.order.preRedirectNotice')}</p>
                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={handlePlaceCardOrder}
                        disabled={cardOrderSubmitting || !canWrite}
                        title={canWrite ? undefined : t("readOnly.disabledHint")}
                        className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50"
                      >
                        {cardOrderSubmitting ? t('cardDesign.order.ordering') : t('cardDesign.order.orderButton')}
                      </button>
                    </div>
                    {cardOrderError && <div className="text-red-600 text-center text-sm">{cardOrderError}</div>}

                    <div className="pt-4">
                      <h4 className="font-medium text-gray-700 mb-2">{t('cardDesign.order.history.title')}</h4>
                      {cardOrderHistoryLoading ? (
                        <p className="text-sm text-gray-500">{t('common.loading')}</p>
                      ) : !cardOrderHistory || cardOrderHistory.length === 0 ? (
                        <p className="text-sm text-gray-500">{t('cardDesign.order.history.empty')}</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm text-left">
                            <thead>
                              <tr className="text-gray-500 border-b">
                                <th className="py-1 pr-4">{t('cardDesign.order.history.date')}</th>
                                <th className="py-1 pr-4">{t('cardDesign.order.history.quantity')}</th>
                                <th className="py-1 pr-4">{t('cardDesign.order.history.status')}</th>
                                <th className="py-1 pr-4">{t('cardDesign.order.history.shippingAddress')}</th>
                                {isAdmin && <th className="py-1 pr-4">{t('cardDesign.order.history.orderedBy')}</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {cardOrderHistory.map((order) => (
                                <tr key={order.id} className="border-b last:border-0">
                                  <td className="py-1 pr-4">{new Date(order.createdAt).toLocaleDateString()}</td>
                                  <td className="py-1 pr-4">{order.quantity}</td>
                                  <td className="py-1 pr-4">
                                    {order.status === 'paid' ? t('cardDesign.order.history.statusPaid') : t('cardDesign.order.history.statusPending')}
                                    {order.isWelcomeGift && (
                                      <span className="ml-2 px-1.5 py-0.5 rounded text-xs bg-teal-100 text-teal-700">
                                        {t('cardDesign.order.history.free')}
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-1 pr-4">{order.shippingAddress}</td>
                                  {isAdmin && (
                                    <td className="py-1 pr-4">{order.orderedBy?.name || order.orderedBy?.email || '-'}</td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Accordion: Danger Zone (Admin only) */}
          {isAdmin && (
            <div className="bg-white rounded-lg shadow p-6 mt-6 border border-red-200">
              <button
                className="w-full flex justify-between items-center px-4 py-3 bg-red-50 rounded-t-lg focus:outline-none"
                onClick={() => setOpenSection(openSection === 'danger' ? null : 'danger')}
                aria-expanded={openSection === 'danger'}
              >
                <span className="font-semibold text-red-700">{t('restaurant.dangerZone')}</span>
                <svg
                  className={`w-5 h-5 transform transition-transform text-red-700 ${openSection === 'danger' ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openSection === 'danger' && (
                <div className="pt-4">
                  <h2 className="text-lg font-semibold text-red-700 mb-2">{t('restaurant.deleteRestaurantTitle')}</h2>
                  <p className="text-sm text-gray-600 mb-4">{t('restaurant.deleteRestaurantWarning')}</p>
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    {t('restaurant.deleteRestaurantButton')}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <h2 className="text-xl font-semibold mb-2">{t('restaurant.noRestaurantConnected')}</h2>
          {googleStatus?.connected && !googleStatus.needsReconnect ? (
            // Connected but no restaurant yet: the Add location form offers
            // their Google listings, so send them there.
            <>
              <p className="text-gray-600 mb-4">{t('restaurant.google.emptyConnectedHint', { email: googleStatus.email || '' })}</p>
              <button
                onClick={() => setShowAddLocation(true)}
                className="inline-block px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors"
              >
                {t('restaurant.google.emptyConnectedButton')}
              </button>
            </>
          ) : (
          <>
          <p className="text-gray-600 mb-4">{t('restaurant.connectGoogleHint')}</p>
          <button
            onClick={handleConnectGoogle}
            className="inline-block px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors"
            disabled={oauthLoading}
          >
            {oauthLoading ? t('restaurant.connecting') : t('restaurant.connectGoogle')}
          </button>
          {googleError && <p className="mt-3 text-sm text-red-700">{googleError}</p>}
          </>
          )}
          <div className="mt-3">
            <button
              onClick={() => setShowAddLocation(true)}
              className="text-sm text-teal-700 hover:underline"
            >
              {t('restaurant.addManually')}
            </button>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md space-y-4">
            <h3 className="text-lg font-bold text-red-700">{t('restaurant.deleteModalTitle')}</h3>
            <p className="text-sm text-gray-600">{t('restaurant.deleteModalWarning')}</p>
            <ul className="text-sm text-gray-600 list-disc pl-5 space-y-1">
              <li>{t('restaurant.deleteModalItemReviews')}</li>
              <li>{t('restaurant.deleteModalItemManagers')}</li>
              <li>{t('restaurant.deleteModalItemQr')}</li>
              <li>{t('restaurant.deleteModalItemGoogle')}</li>
            </ul>
            <p className="text-sm text-gray-700">
              {t('restaurant.deleteModalTypeToConfirm')}{' '}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-red-700">{DELETE_CONFIRM_PHRASE}</code>
            </p>
            <input
              type="text"
              value={deleteConfirmInput}
              onChange={(e) => setDeleteConfirmInput(e.target.value)}
              disabled={deleting}
              className="w-full border rounded px-3 py-2"
              autoFocus
            />
            {deleteError && <div className="text-sm text-red-600">{deleteError}</div>}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmInput('');
                  setDeleteError(null);
                }}
                disabled={deleting}
                className="flex-1 py-2 rounded border font-semibold text-gray-700"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleDeleteRestaurant}
                disabled={deleting || deleteConfirmInput !== DELETE_CONFIRM_PHRASE}
                className="flex-1 bg-red-600 text-white py-2 rounded font-semibold disabled:opacity-50"
              >
                {deleting ? t('restaurant.deleting') : t('restaurant.deleteRestaurantButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteQrModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md space-y-4">
            <h3 className="text-lg font-bold text-red-700">{t('restaurant.deleteQrModalTitle')}</h3>
            <p className="text-sm text-gray-600">{t('restaurant.deleteQrModalWarning')}</p>
            <p className="text-sm text-gray-700">
              {t('restaurant.deleteModalTypeToConfirm')}{' '}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-red-700">{DELETE_QR_CONFIRM_PHRASE}</code>
            </p>
            <input
              type="text"
              value={deleteQrConfirmInput}
              onChange={(e) => setDeleteQrConfirmInput(e.target.value)}
              disabled={deletingQr}
              className="w-full border rounded px-3 py-2"
              autoFocus
            />
            {deleteQrError && <div className="text-sm text-red-600">{deleteQrError}</div>}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteQrModal(false);
                  setDeleteQrConfirmInput('');
                  setDeleteQrError(null);
                }}
                disabled={deletingQr}
                className="flex-1 py-2 rounded border font-semibold text-gray-700"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleDeleteQr}
                disabled={deletingQr || deleteQrConfirmInput !== DELETE_QR_CONFIRM_PHRASE}
                className="flex-1 bg-red-600 text-white py-2 rounded font-semibold disabled:opacity-50"
              >
                {deletingQr ? t('restaurant.deleting') : t('restaurant.deleteQrButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Restaurant;